/**
 * Kanal WhatsApp — K207 W6 (SESI-58 U4). `docs/SPEK_WA_CUSTOMER_JOURNEY.md` §14c–§14e.
 *
 * Lima bagian, dibuka sesuai peran (cermin `backend/routers/wa_ketahanan.py` + `wa_laporan.py`):
 *   1. Keadaan kanal        Owner · KC · CS (KC/CS: cabang sendiri)
 *   2. Pesan belum terkirim  Owner · KC · CS — F1: "Ambil untuk kirim manual" MENANDAI dulu, baru menyalin teksnya,
 *                            supaya pesan itu tidak terkirim lagi saat layanan pulih (customer tidak menerima dua kali)
 *   2b. Seberapa sering CS mengambil alih  Owner · KC (KC: cabang sendiri) — §9 metrik supervisi, hanya angka
 *   3. Pindah nomor cabang   Owner — F3: nomor diblokir → pindah ke nomor cadangan, lalu kembalikan; juga isian
 *                            nomor WhatsApp untuk tombol "Tanya lewat WhatsApp" di Ruang Pesanan (per nomor cabang)
 *   4. Latihan               Owner, hanya mode uji — saklar latihan F1 + pesan uji ke dua nomor izin
 *   0. WhatsApp penting yang belum sampai (BUSINESS-100) + Status WhatsApp ke customer (T45) — Owner · KC · CS,
 *      `components/etams/TugasWaBelumSampai.jsx` (`backend/routers/wa_status.py`)
 *
 * ⛔ Membalas customer tetap di Cekat (lihat Percakapan). Layar ini hanya untuk keadaan darurat kanal.
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { apiErrorMessage } from "@/lib/inventoryApi";
import { formatDateTime } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";
import { konfirmasi } from "@/lib/dialogAplikasi";
import Hint from "@/components/etams/Hint";
import LaporanAmbilAlihWa from "@/components/etams/LaporanAmbilAlihWa";
import { DaftarStatusWaTerbaru, DaftarTugasWa } from "@/components/etams/TugasWaBelumSampai";
import { BusyButton } from "@/components/etams/BusyButton";
import { Paginasi } from "@/components/etams/PalangDaftar";
import { useDaftar } from "@/hooks/useDaftar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

// Kode keadaan → kata yang dibaca staf (K200 butir 6).
const KATA_CEKAT = {
  ok: "Tersambung",
  jaringan: "Tidak terjangkau",
  server: "Layanan sedang bermasalah",
  tolak: "Pemeriksaan ditolak layanan (perlu dicek Owner)",
  belum_diatur: "Belum diatur di server",
};
const KATA_STATUS = { PENDING: "Menunggu dikirim", FAILED: "Gagal terkirim" };
// T63 (26 Okt 2026): keadaan tiap pesan di kotak "Pesan belum terkirim" (server: wa_ketahanan.keadaan_antrean).
const KATA_KEADAAN = {
  MENUNGGU: "Belum dikirim ke layanan WhatsApp. Bila mendesak, ambil untuk dikirim manual.",
  GAGAL: "Gagal — hubungi customer lewat telepon, atau salin isinya dan kirim dari WhatsApp kantor.",
  BELUM_PASTI: "Belum pasti sampai — layanan WhatsApp tidak menjawab saat pesan ini dikirim. Tanyakan dulu ke customer "
    + "sebelum mengirim manual, supaya tidak dobel.",
};

function Kotak({ judul, hint, children, testId }) {
  return (
    <section className="rounded-xl border border-border bg-card p-4 space-y-3" data-testid={testId}>
      <h2 className="font-semibold text-foreground">
        {judul} {hint && <Hint>{hint}</Hint>}
      </h2>
      {children}
    </section>
  );
}

function salin(teks) {
  try {
    navigator.clipboard.writeText(teks);
    toast.success("Teks pesan disalin.");
  } catch (e) {
    toast.error("Browser menolak menyalin. Salin teksnya dari layar.");
  }
}

function BagianKeadaan({ k }) {
  if (!k) return <p className="text-sm text-muted-foreground">Memuat…</p>;
  const c = k.cekat || {};
  return (
    <div className="space-y-2 text-sm">
      {k.latihan_f1 && (
        <div className="rounded-lg border border-warning-soft-border bg-warning-soft px-3 py-2 text-warning-soft-foreground"
             data-testid="kanal-latihan-banner">
          LATIHAN F1 sedang berjalan sejak {formatDateTime(k.latihan_f1.mulai)} — pesan ke customer sengaja ditahan.
          Padam otomatis {formatDateTime(k.latihan_f1.padam_otomatis)}.
        </div>
      )}
      {k.antrean_ditahan && !k.latihan_f1 && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-destructive">
          Pengiriman ditahan: {k.antrean_ditahan}. Pesan tetap mengantre dan terkirim sendiri begitu layanan pulih.
        </div>
      )}
      {/* BUSINESS-103 (K225 butir 3): gangguan dari hasil kiriman — CS kirim manual dari WA kantor. */}
      {k.gangguan_kiriman?.kejadian_terbuka && !k.latihan_f1 && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-destructive"
             data-testid="kanal-gangguan-banner">
          Layanan WhatsApp sedang bermasalah
          {k.gangguan_kiriman.gagal_beruntun > 0 && ` (${k.gangguan_kiriman.gagal_beruntun} kiriman berturut-turut gagal)`}.
          Kabar penting di kotak "Pesan belum terkirim" bisa dikirim dari WhatsApp CS kantor: tekan "Ambil untuk kirim
          manual" dulu, lalu kirim teksnya — sistem tidak akan mengirimnya lagi saat layanan pulih.
        </div>
      )}
      <p>
        Layanan WhatsApp (Cekat):{" "}
        <span className="font-medium">{c.jenis ? (KATA_CEKAT[c.jenis] || "Tidak diketahui") : "Belum diperiksa"}</span>
        {c.gagal_beruntun > 0 && ` — gagal ${c.gagal_beruntun} kali berturut-turut`}
        {c.pada && <span className="text-muted-foreground"> · diperiksa {formatDateTime(c.pada)}</span>}
      </p>
      <p>
        Pesan belum terkirim lebih dari {k.ambang?.antrean_menua_menit} menit: <b>{k.antrean?.menua ?? 0}</b> ·
        gagal dalam {k.ambang?.gagal_jam} jam: <b>{k.antrean?.gagal ?? 0}</b>
      </p>
      {/* Satu kartu per nomor cabang (2–3 nomor, struktur tetap — bukan daftar transaksi). */}
      <div className="grid gap-2 sm:grid-cols-2" data-testid="kanal-kanari">
        {(k.kanari || []).map((x, i) => (
          <div key={i} className={`rounded-md border border-border p-2 text-xs ${x.buruk ? "text-destructive" : ""}`}>
            <p className="font-medium">{x.label || "—"}</p>
            <p>Pesan masuk terakhir: {x.terakhir_masuk ? formatDateTime(x.terakhir_masuk) : "Belum pernah"}</p>
            <p>Jam kerja tanpa pesan: {x.sunyi_jam_kerja}</p>
            {x.jam_kerja && <p className="text-muted-foreground">Jam kerja: {x.jam_kerja}</p>}
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">
        Jam kerja cabang: {k.ambang?.jam_kerja}. Peringatan berbunyi bila {k.ambang?.kanari_jam_kerja} jam kerja
        tanpa satu pun pesan masuk (hanya saat sistem memakai data nyata); Kepala Cabang ikut diberi tahu.
      </p>
    </div>
  );
}

function BagianAntrean({ items, muatUlang }) {
  // Saat gangguan antrean bisa ratusan pesan (server mengirim paling banyak 400) — pola daftar seragam.
  const daftar = useDaftar(items, { kunciCari: ["tujuan", "nomor_cabang", "teks"] });
  const [sibuk, setSibuk] = useState(null);
  const [diambil, setDiambil] = useState(null);
  const ambil = async (x) => {
    const ok = await konfirmasi(
      `Ambil pesan ke ${x.tujuan} untuk dikirim manual?\n\nSesudah ini sistem TIDAK akan mengirim pesan ini lagi. ` +
      "Kirim teksnya dari nomor WhatsApp lain sekarang juga.",
      { judul: "Kirim manual", ya: "Ambil dan salin" });
    if (!ok) return;
    setSibuk(x.id);
    try {
      const { data } = await api.post(`/wa-ketahanan/antrean/${x.id}/dikirim-manual`, {});
      setDiambil(data);
      salin(data.teks || "");
      muatUlang();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Pesan gagal diambil."));
    } finally {
      setSibuk(null);
    }
  };
  return (
    <div className="space-y-3">
      {diambil && (
        <div className="rounded-lg border border-success/40 bg-success/10 px-3 py-2 text-sm" data-testid="kanal-diambil">
          <p className="font-medium">Kirim sekarang ke {diambil.tujuan}:</p>
          <p className="whitespace-pre-wrap break-words">{diambil.teks}</p>
          {(diambil.tautan || []).map((u) => <p key={u} className="break-all text-xs">{u}</p>)}
          <button type="button" className="mt-1 text-xs underline" onClick={() => salin(diambil.teks || "")}>Salin lagi</button>
        </div>
      )}
      {items.length === 0 && <p className="text-sm text-muted-foreground">Tidak ada pesan yang tertahan.</p>}
      {daftar.tampil.map((x) => (
        <div key={x.id} className="rounded-lg border border-border p-3 text-sm space-y-1" data-testid="kanal-antrean-baris">
          <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
            <span className="font-medium text-foreground">{x.tujuan}</span>
            <span>{x.nomor_cabang || "—"}</span>
            <span>{formatDateTime(x.dibuat)}</span>
            <span className="rounded border border-border px-1.5">
              {x.keadaan === "PERJALANAN" ? "Sedang dikirim" : (KATA_STATUS[x.status] || "—")}
            </span>
            {x.sebab && <span>· {x.sebab}</span>}
          </div>
          <p className="whitespace-pre-wrap break-words">{x.teks || "—"}</p>
          {/* T63: kalimat mengikuti keadaan pesan — "dalam perjalanan" hanya untuk yang masih diantar layanan WA. */}
          {x.sedang_dikirim ? (
            <p className="text-xs text-muted-foreground" data-testid="kanal-antrean-keadaan">
              Sedang dalam perjalanan ke customer — jangan dikirim manual.
            </p>
          ) : (
            <>
              {KATA_KEADAAN[x.keadaan] && (
                <p className={`text-xs ${x.keadaan === "MENUNGGU" ? "text-muted-foreground" : "text-destructive"}`}
                   data-testid="kanal-antrean-keadaan">{KATA_KEADAAN[x.keadaan]}</p>
              )}
              <BusyButton size="sm" variant="outline" busy={sibuk === x.id} onClick={() => ambil(x)}
                          data-testid="kanal-ambil-manual">Ambil untuk kirim manual</BusyButton>
            </>
          )}
        </div>
      ))}
      <Paginasi daftar={daftar} testId="kanal-antrean-paginasi" />
    </div>
  );
}

/* Nomor yang dibuka customer lewat tombol "Tanya lewat WhatsApp" di Ruang Pesanan — diisi Owner, per nomor cabang.
   Kosong = tombol tidak tampil (customer hanya membaca "balas chat WhatsApp"). Server merapikan 0812…/+62… ke 62…. */
function NomorUntukCustomer({ p, onTersimpan }) {
  const [nomor, setNomor] = useState(p.nomor_wa_publik || "");
  const [sibuk, setSibuk] = useState(false);
  const simpan = async () => {
    setSibuk(true);
    try {
      const { data } = await api.post("/wa-ketahanan/inbox/nomor",
        { inbox_id: p.inbox_id, nomor_wa_publik: nomor.trim() || null });
      setNomor(data.nomor_wa_publik || "");
      toast.success(data.nomor_wa_publik ? "Nomor untuk customer disimpan." : "Nomor untuk customer dikosongkan.");
      onTersimpan();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Nomor gagal disimpan."));
    } finally {
      setSibuk(false);
    }
  };
  return (
    <div className="mt-2 space-y-1" data-testid="kanal-nomor-customer">
      <p className="text-muted-foreground">
        Nomor WhatsApp untuk tombol "Tanya lewat WhatsApp" di Ruang Pesanan{p.nomor_wa_publik ? "" : " (belum diisi — tombol tidak tampil)"}:
      </p>
      <div className="flex gap-2">
        <Input className="h-8 text-xs" placeholder="mis. 0812 3456 7890" value={nomor}
               onChange={(e) => setNomor(e.target.value)} data-testid="kanal-nomor-customer-isi" />
        <BusyButton size="sm" variant="outline" busy={sibuk} onClick={simpan}
                    disabled={(nomor.trim() || null) === (p.nomor_wa_publik || null)}
                    data-testid="kanal-nomor-customer-simpan">Simpan</BusyButton>
      </div>
    </div>
  );
}

function BagianPindahNomor() {
  const [d, setD] = useState(null);
  const [isi, setIsi] = useState({ region_id: "", inbox_id: "", label: "", alasan: "" });
  const [sibuk, setSibuk] = useState(false);
  const muat = useCallback(async () => {
    try {
      const { data } = await api.get("/wa-ketahanan/inbox");
      setD(data);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Daftar nomor cabang gagal dimuat."));
    }
  }, []);
  useEffect(() => { muat(); }, [muat]);
  const pindah = async () => {
    const ok = await konfirmasi("Pindahkan cabang ini ke nomor WhatsApp yang dipilih? Nomor lama dinonaktifkan dan bisa "
      + "dikembalikan dengan cara yang sama.", { judul: "Pindah nomor cabang", ya: "Pindahkan", bahaya: true });
    if (!ok) return;
    setSibuk(true);
    try {
      await api.post("/wa-ketahanan/inbox/pindah", isi);
      toast.success("Nomor cabang dipindahkan.");
      setIsi({ region_id: "", inbox_id: "", label: "", alasan: "" });
      muat();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Pemindahan gagal."));
    } finally {
      setSibuk(false);
    }
  };
  if (!d) return <p className="text-sm text-muted-foreground">Memuat…</p>;
  return (
    <div className="space-y-3 text-sm">
      {/* Satu kartu per nomor (aktif + cadangan per cabang — struktur tetap, bukan daftar transaksi). */}
      <div className="grid gap-2 sm:grid-cols-2" data-testid="kanal-pemetaan">
        {d.pemetaan.map((p) => (
          <div key={p.inbox_id} className="rounded-md border border-border p-2 text-xs">
            <p className="font-medium">{p.region || "—"} · {p.aktif ? "Aktif" : "Cadangan"}</p>
            <p>Nama nomor: {p.label || "—"}</p>
            <p className="break-all text-muted-foreground">Kode nomor di Cekat: {p.inbox_id}</p>
            {d.nomor_tersedia ? <NomorUntukCustomer p={p} onTersimpan={muat} /> : null}
          </div>
        ))}
      </div>
      {d.nomor_tersedia ? null : (
        <p className="text-xs text-muted-foreground" data-testid="kanal-nomor-customer-menunggu">
          Isian nomor WhatsApp untuk tombol di Ruang Pesanan belum bisa dipakai — pembaruan sistemnya belum diterapkan.
        </p>
      )}
      <div className="grid gap-2 sm:grid-cols-2">
        <select className="h-9 rounded-md border border-input bg-background px-2" value={isi.region_id}
                onChange={(e) => setIsi({ ...isi, region_id: e.target.value })} data-testid="kanal-pindah-cabang">
          <option value="">Pilih cabang…</option>
          {d.region.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
        {d.cekat?.terbaca && d.cekat.inbox.length > 0 ? (
          <select className="h-9 rounded-md border border-input bg-background px-2" value={isi.inbox_id}
                  onChange={(e) => setIsi({ ...isi, inbox_id: e.target.value })} data-testid="kanal-pindah-nomor">
            <option value="">Pilih nomor di Cekat…</option>
            {d.cekat.inbox.map((x) => <option key={x.id} value={x.id}>{x.nama || x.id} {x.nomor ? `(${x.nomor})` : ""}</option>)}
          </select>
        ) : (
          <Input placeholder="Kode nomor di Cekat (lihat dashboard Cekat)" value={isi.inbox_id}
                 onChange={(e) => setIsi({ ...isi, inbox_id: e.target.value })} data-testid="kanal-pindah-nomor" />
        )}
        <Input placeholder="Nama nomor (mis. WA cadangan Samarinda)" value={isi.label}
               onChange={(e) => setIsi({ ...isi, label: e.target.value })} />
      </div>
      <Textarea placeholder="Alasan (wajib, mis. nomor Samarinda diblokir WhatsApp sejak 10.15)" value={isi.alasan}
                onChange={(e) => setIsi({ ...isi, alasan: e.target.value })} data-testid="kanal-pindah-alasan" />
      <BusyButton busy={sibuk} disabled={!isi.region_id || !isi.inbox_id || isi.alasan.trim().length < 10}
                  onClick={pindah} data-testid="kanal-pindah-kirim">Pindahkan</BusyButton>
    </div>
  );
}

function BagianLatihan({ muatUlang }) {
  const [d, setD] = useState(null);
  const [region, setRegion] = useState([]);
  const [alasan, setAlasan] = useState("");
  const [uji, setUji] = useState({ nomor: "", region_id: "", latihan: "F1", ke: 1 });
  const [sibuk, setSibuk] = useState(false);
  const muat = useCallback(async () => {
    try {
      const [l, i] = await Promise.all([api.get("/wa-ketahanan/latihan"), api.get("/wa-ketahanan/inbox")]);
      setD(l.data);
      setRegion(i.data.region || []);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Keadaan latihan gagal dimuat."));
    }
  }, []);
  useEffect(() => { muat(); }, [muat]);
  const saklar = async (nyala) => {
    setSibuk(true);
    try {
      await api.post("/wa-ketahanan/latihan/f1", { nyala, alasan });
      toast.success(nyala ? "Latihan F1 menyala." : "Latihan F1 dipadamkan — antrean terkirim di putaran berikutnya.");
      setAlasan("");
      muat();
      muatUlang();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Saklar latihan gagal diubah."));
    } finally {
      setSibuk(false);
    }
  };
  const kirimUji = async () => {
    setSibuk(true);
    try {
      await api.post("/wa-ketahanan/latihan/pesan-uji", { ...uji, ke: Number(uji.ke) });
      toast.success("Pesan uji masuk antrean.");
      muatUlang();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Pesan uji gagal dibuat."));
    } finally {
      setSibuk(false);
    }
  };
  if (!d) return <p className="text-sm text-muted-foreground">Memuat…</p>;
  if (!d.boleh) {
    return <p className="text-sm text-muted-foreground">Latihan hanya tersedia di mode uji. Mode sekarang: {d.mode}.</p>;
  }
  return (
    <div className="space-y-3 text-sm">
      <p className="text-xs text-muted-foreground">
        Naskah lengkap: docs/perjalanan/DRILL-WA-F1-F3.md. Latihan F1 padam sendiri sesudah {d.padam_otomatis_menit} menit.
      </p>
      {d.latihan_f1 ? (
        <div className="space-y-2">
          <p>Latihan F1 menyala sejak {formatDateTime(d.latihan_f1.mulai)} oleh {d.latihan_f1.oleh || "—"}.</p>
          <BusyButton busy={sibuk} variant="outline" onClick={() => saklar(false)} data-testid="kanal-latihan-padam">
            Padamkan latihan F1
          </BusyButton>
        </div>
      ) : (
        <div className="space-y-2">
          <Input placeholder="Alasan latihan (wajib)" value={alasan} onChange={(e) => setAlasan(e.target.value)}
                 data-testid="kanal-latihan-alasan" />
          <BusyButton busy={sibuk} disabled={alasan.trim().length < 10} onClick={() => saklar(true)}
                      data-testid="kanal-latihan-nyala">Nyalakan latihan F1</BusyButton>
        </div>
      )}
      <div className="grid gap-2 sm:grid-cols-4">
        <select className="h-9 rounded-md border border-input bg-background px-2" value={uji.nomor}
                onChange={(e) => setUji({ ...uji, nomor: e.target.value })} data-testid="kanal-uji-nomor">
          <option value="">Nomor uji…</option>
          {(d.nomor_izin || []).map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
        <select className="h-9 rounded-md border border-input bg-background px-2" value={uji.region_id}
                onChange={(e) => setUji({ ...uji, region_id: e.target.value })}>
          <option value="">Wilayah…</option>
          {region.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
        <select className="h-9 rounded-md border border-input bg-background px-2" value={uji.latihan}
                onChange={(e) => setUji({ ...uji, latihan: e.target.value })}>
          <option value="F1">Latihan F1</option>
          <option value="F3">Latihan F3</option>
        </select>
        <select className="h-9 rounded-md border border-input bg-background px-2" value={uji.ke}
                onChange={(e) => setUji({ ...uji, ke: e.target.value })}>
          {[1, 2, 3].map((n) => <option key={n} value={n}>Pesan ke-{n}</option>)}
        </select>
      </div>
      <BusyButton busy={sibuk} disabled={!uji.nomor || !uji.region_id} onClick={kirimUji} data-testid="kanal-uji-kirim">
        Buat pesan uji
      </BusyButton>
    </div>
  );
}

export default function KanalWhatsapp() {
  const { profile } = useAuth();
  const isOwner = profile?.role?.code === "OWNER";
  const bolehLaporan = isOwner || profile?.role?.code === "KEPALA_CABANG";
  const [k, setK] = useState(null);
  const [items, setItems] = useState([]);
  const [galat, setGalat] = useState("");
  // T62: hasil tugas CS yang baru dicatat langsung terlihat di "Status WhatsApp ke customer" dan kotak antrean.
  const [versi, setVersi] = useState(0);

  const muat = useCallback(async () => {
    setGalat("");
    try {
      const [a, b] = await Promise.all([api.get("/wa-ketahanan/keadaan"), api.get("/wa-ketahanan/antrean")]);
      setK(a.data);
      setItems(b.data.items || []);
    } catch (e) {
      setGalat(apiErrorMessage(e, "Keadaan kanal WhatsApp gagal dimuat."));
    }
  }, []);
  const sesudahTugas = useCallback(() => { setVersi((v) => v + 1); muat(); }, [muat]);
  useEffect(() => { muat(); }, [muat]);

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold text-foreground">
        Kanal WhatsApp{" "}
        <Hint>Keadaan nomor WhatsApp cabang dan pesan yang belum terkirim. Membalas customer tetap lewat Cekat.</Hint>
      </h1>
      {galat && (
        <div role="alert" className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {galat}
        </div>
      )}
      <Kotak judul="WhatsApp penting yang belum sampai" testId="kanal-tugas-wa"
             hint="Tautan bayar, tautan Ruang Pesanan, FA siap dikonfirmasi, kwitansi, dan kabar pesanan selesai yang gagal atau 15 menit belum sampai di HP customer. Hubungi customer lewat telepon atau kirim ulang isi pesannya, lalu catat hasilnya. Satu jam kerja belum ditangani, Kepala Cabang ikut diberi tahu.">
        <DaftarTugasWa onBerubah={sesudahTugas} />
      </Kotak>
      <Kotak judul="Keadaan kanal" testId="kanal-keadaan">
        <BagianKeadaan k={k} />
      </Kotak>
      <Kotak judul="Status WhatsApp ke customer" testId="kanal-status-wa"
             hint="Tiap pesan 7 hari terakhir. &quot;Terkirim&quot; belum berarti sampai — customer baru menerimanya bila tertulis &quot;Sampai di HP&quot; atau &quot;Sudah dibaca&quot;.">
        <DaftarStatusWaTerbaru versi={versi} />
      </Kotak>
      <Kotak judul="Pesan belum terkirim" testId="kanal-antrean"
             hint="Saat layanan WhatsApp gangguan, pesan menunggu di sini dan terkirim sendiri begitu pulih. Pesan penting bisa diambil untuk dikirim manual dari nomor lain.">
        <BagianAntrean items={items} muatUlang={sesudahTugas} />
      </Kotak>
      {bolehLaporan && (
        <Kotak judul="Seberapa sering CS mengambil alih" testId="kanal-ambil-alih"
               hint="Tiap kali AI meneruskan percakapan ke CS tercatat di sini. Dipakai untuk menilai di langkah pesanan mana AI sudah bisa dilepas sendiri, dan di mana masih sering butuh CS.">
          <LaporanAmbilAlihWa isOwner={isOwner} />
        </Kotak>
      )}
      {isOwner && (
        <Kotak judul="Pindah nomor cabang" testId="kanal-pindah"
               hint="Dipakai bila nomor cabang diblokir WhatsApp: daftarkan nomor cadangan di Cekat dulu, lalu pindahkan cabangnya ke sini. Di tiap kartu nomor juga diisi nomor WhatsApp yang dibuka customer dari tombol &quot;Tanya lewat WhatsApp&quot; di Ruang Pesanan — isi juga untuk nomor cadangan, karena tombolnya mengikuti nomor yang sedang aktif.">
          <BagianPindahNomor />
        </Kotak>
      )}
      {isOwner && (
        <Kotak judul="Latihan ketahanan" testId="kanal-latihan">
          <BagianLatihan muatUlang={muat} />
        </Kotak>
      )}
    </div>
  );
}
